'use client';

import type { ReactNode } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import {
  BodyStrong,
  MetaDefault,
  MonoSmall,
} from '../../../components/typography';

type ScoreSummaryRowProps = {
  baseScoreLabel: string;
  baseScore: string;
  baseScoreColor: string;
  vectorLabel: string;
  vector: string;
  emptyVectorLabel: string;
  environmentalScoreLabel?: string;
  environmentalScore?: string;
  environmentalScoreColor?: string;
  baseScoreFooter?: ReactNode;
  vectorFooter?: ReactNode;
};

function ScoreCell({
  label,
  score,
  scoreColor,
  footer,
}: {
  label: string;
  score: string;
  scoreColor: string;
  footer?: ReactNode;
}) {
  return (
    <VStack gap={1.5} alignItems="flex-start" minW="60px">
      <VStack gap={0} alignItems="flex-start" minW="60px">
        <MetaDefault color="fg.muted">{label}</MetaDefault>
        <BodyStrong color={scoreColor}>{score || '-'}</BodyStrong>
      </VStack>
      {footer}
    </VStack>
  );
}

export function ScoreSummaryRow({
  baseScoreLabel,
  baseScore,
  baseScoreColor,
  vectorLabel,
  vector,
  emptyVectorLabel,
  environmentalScoreLabel,
  environmentalScore,
  environmentalScoreColor,
  baseScoreFooter,
  vectorFooter,
}: ScoreSummaryRowProps) {
  const showEnvironmentalScore =
    environmentalScoreLabel != null && environmentalScoreColor != null;

  return (
    <HStack
      gap={3}
      w="full"
      alignItems="center"
      p={2}
      bg="bg.subtle"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.subtle"
      flexWrap="wrap"
    >
      {showEnvironmentalScore ? (
        <>
          <ScoreCell
            label={environmentalScoreLabel}
            score={environmentalScore ?? ''}
            scoreColor={environmentalScoreColor}
          />
          <Box h="8" w="1px" bg="border.subtle" />
        </>
      ) : null}

      <VStack gap={1.5} alignItems="flex-start" flex={1} minW={0}>
        <VStack gap={0} alignItems="flex-start" flex={1} minW={0}>
          <MetaDefault color="fg.muted">{vectorLabel}</MetaDefault>
          <MonoSmall
            color={vector ? 'fg.default' : 'fg.muted'}
            wordBreak="break-all"
            lineHeight="1.3"
          >
            {vector || emptyVectorLabel}
          </MonoSmall>
        </VStack>
        {vectorFooter}
      </VStack>

      <Box h="8" w="1px" bg="border.subtle" alignSelf="flex-start" />

      <ScoreCell
        label={baseScoreLabel}
        score={baseScore}
        scoreColor={baseScoreColor}
        footer={baseScoreFooter}
      />
    </HStack>
  );
}
